/**
 * ProfileAvatar — 个人资料头像（ADM-A9 自助）显示 + 上传
 *
 * 头像存 admin 自有 BUCKET、经鉴权代理端点读取（GET /api/v1/me/avatar，需 admin JWT），故**必须**走
 * authedFetch（裸 img 不带 Bearer → 401）；authedFetch→blob→objectURL→<img>，404 回退首字母。
 * 上传：file input → POST multipart /api/v1/me/avatar（authedFetch，浏览器自带 multipart boundary）→ 重取。
 */

import { useEffect, useRef, useState } from 'react';
import { authedFetch } from '~/api/session-refresh';
import { Icons } from '~/lib/icons';
import { newRequestId } from '~/lib/request-id';
import { toast } from '~/ui/Toast';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const AVATAR_URL = `${API_BASE || ''}/api/v1/me/avatar`;

export function ProfileAvatar({ initial }: { initial: string }) {
  const [objUrl, setObjUrl] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey 是上传成功后的「重取」触发器（递增即重跑），非 effect 体内引用
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    (async () => {
      try {
        const res = await authedFetch(AVATAR_URL, {
          method: 'GET',
          headers: { 'X-Request-Id': newRequestId() },
        });
        if (!res.ok) return; // 404 = 未设头像 → 保持首字母
        const blob = await res.blob();
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setObjUrl(created);
      } catch {
        // 网络/解码失败 → 保持首字母兜底
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [reloadKey]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许重选同名文件
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await authedFetch(AVATAR_URL, {
        method: 'POST',
        headers: { 'X-Request-Id': newRequestId() },
        body: form,
      });
      if (!res.ok) throw new Error('上传失败');
      toast('头像已更新', { tone: 'pos', icon: 'IconCheck' });
      setObjUrl(null);
      setReloadKey((k) => k + 1); // 触发重取最新头像
    } catch (err) {
      toast(err instanceof Error ? err.message : '上传失败', { tone: 'neg', icon: 'IconAlert' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="hstack" style={{ gap: 12 }}>
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: 999,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, oklch(0.6 0.18 35), oklch(0.45 0.14 320))',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          fontWeight: 600,
        }}
      >
        {objUrl ? (
          <img
            src={objUrl}
            alt="头像"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          initial
        )}
      </div>
      <div className="vstack" style={{ gap: 4 }}>
        <button
          type="button"
          className="btn btn-sm"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <Icons.IconUpload size={11} />
          {uploading ? '上传中…' : '更换头像'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={onPick}
        />
      </div>
    </div>
  );
}
